import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Info } from "lucide-react";
import { Employee } from "../types/employee";
import { cn } from "../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface EmployeeDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee | null;
}

type FilterPeriod = "all" | "today" | "week" | "month" | "year";

const EmployeeDetailsSidebar: React.FC<EmployeeDetailsSidebarProps> = ({
  isOpen,
  onClose,
  employee,
}) => {
  const [activeTab, setActiveTab] = useState<"overview" | "personal" | "workspace" | "pay">("overview");
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>("all");

  if (!employee) {
    return null;
  }

  // Get all employee bookings
  const allEmployeeBookings = (employee as any)?.employeeBookings || [];

  // Filter bookings based on selected period
  const filterBookingsByPeriod = (bookings: any[], period: FilterPeriod): any[] => {
    if (period === "all") {
      return bookings;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return bookings.filter((booking: any) => {
      // Use created_at first, fallback to updated_at
      const bookingDateStr = booking.created_at || booking.updated_at;

      // If no date available, exclude from date-based filters
      if (!bookingDateStr) {
        return false;
      }

      const bookingDate = new Date(bookingDateStr);
      bookingDate.setHours(0, 0, 0, 0);

      switch (period) {
        case "today":
          return bookingDate.getTime() === today.getTime();

        case "week": {
          // Get the start of the current week (Monday)
          const dayOfWeek = now.getDay();
          const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust when day is Sunday
          const weekStart = new Date(now.getFullYear(), now.getMonth(), diff);
          weekStart.setHours(0, 0, 0, 0);
          return bookingDate >= weekStart;
        }

        case "month": {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          return bookingDate >= monthStart;
        }

        case "year": {
          const yearStart = new Date(now.getFullYear(), 0, 1);
          return bookingDate >= yearStart;
        }

        default:
          return true;
      }
    });
  };

  // Get filtered bookings
  const employeeBookings = filterBookingsByPeriod(allEmployeeBookings, filterPeriod);

  // Calculate total sales by summing all amounts from filtered bookings
  const totalSales = employeeBookings.reduce((sum: number, booking: any) => {
    return sum + (parseFloat(booking.amount) || 0);
  }, 0);

  // Calculate unique appointments by counting unique booking_ids from filtered bookings
  const uniqueBookingIds = new Set(
    employeeBookings
      .map((booking: any) => booking.booking_id)
      .filter((id: any) => id !== null && id !== undefined)
  );
  const totalAppointments = uniqueBookingIds.size;

  // Get filter period label for display
  const getFilterLabel = (period: FilterPeriod): string => {
    switch (period) {
      case "all":
        return "Total sales from all bookings";
      case "today":
        return "Sales from today";
      case "week":
        return "Sales from week to date";
      case "month":
        return "Sales from this month";
      case "year":
        return "Sales from this year";
      default:
        return "Total sales from bookings";
    }
  };

  // Get appointments label for display
  const getAppointmentsLabel = (period: FilterPeriod): string => {
    switch (period) {
      case "all":
        return "Total bookings";
      case "today":
        return "Bookings from today";
      case "week":
        return "Bookings from week to date";
      case "month":
        return "Bookings from this month";
      case "year":
        return "Bookings from this year";
      default:
        return "Total bookings";
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatMobile = (mobile: string | null, callingCode: string | null) => {
    if (!mobile) return "Not provided";
    if (callingCode) return `${callingCode} ${mobile}`;
    return mobile;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1000] flex justify-end">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] z-[999]"
          />

          {/* Sidebar Panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="relative h-screen w-full lg:w-[85vw] lg:max-w-5xl bg-white shadow-2xl z-[1001] flex flex-col lg:flex-row"
          >
            {/* Standardized Close Button (Matches BookingEditDrawer) */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute lg:-left-12 right-4 top-4 lg:right-auto lg:top-10 w-11 h-11 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-md hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
              aria-label="Close"
            >
              <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </motion.button>

            <div className="relative h-full w-full flex flex-col lg:flex-row">
              {/* Left Panel Sidebar */}
              <div className="w-full lg:w-[32%] border-b lg:border-r border-gray-100 bg-white flex flex-col shrink-0 overflow-hidden">
                {/* Employee Header (Matches Image) */}
                <div className="p-8 border-b border-gray-50 flex items-center gap-5 bg-gray-50/30">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-[#F1ECFB] border-2 border-white flex items-center justify-center text-[#885ABB] font-bold text-2xl overflow-hidden shadow-sm shrink-0">
                    {employee.image && employee.image !== "https://salonify.co.uk/img/default-avatar-user.png" ? (
                      <img src={employee.image} alt={employee.name} className="w-full h-full object-cover" />
                    ) : (
                      getInitials(employee.name)
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg lg:text-xl font-bold text-[#402B69] truncate leading-tight">
                        {employee.name}
                      </h2>
                      {employee.pin_protected && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200 shrink-0">
                          PIN protected
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-bold text-[#885ABB] uppercase tracking-wider mt-1">
                      {employee.role}
                    </p>
                  </div>
                </div>

                {/* Vertical Navigation Tabs */}
                <div className="flex-1 overflow-y-auto py-8 flex flex-col text-[15px] font-medium">
                  {[
                    { id: "overview", label: "Overview" },
                    { id: "personal", label: "Personal" },
                    { id: "workspace", label: "Workspace" },
                    { id: "pay", label: "Pay" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`group relative flex items-center px-8 py-4 text-left transition-all duration-200 border-r-4 ${activeTab === tab.id
                        ? "text-[#885ABB] font-bold bg-[#F8F5FF] border-[#885ABB]"
                        : "text-gray-500 hover:text-[#885ABB] hover:bg-gray-50 border-transparent"
                        }`}
                    >
                      <span className="transition-transform group-hover:translate-x-1 duration-200">
                        {tab.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right panel content area */}
              <div className="flex-1 flex flex-col min-w-0 bg-[#F9F9F9] overflow-hidden">
                {/* Standardized Internal Header */}
                <div className="bg-white border-b border-gray-100 px-6 sm:px-10 py-8 shrink-0">
                  <p className="text-[11px] text-gray-400 font-bold uppercase tracking-widest mb-1.5 px-0.5">
                    Viewing Member Profile
                  </p>
                  <h2 className="text-2xl sm:text-[32px] font-bold text-[#402B69] leading-tight">
                    {activeTab === "overview" && "Overview"}
                    {activeTab === "personal" && "Personal Information"}
                    {activeTab === "workspace" && "Workspace Details"}
                    {activeTab === "pay" && "Pay & Compensation"}
                  </h2>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar px-6 sm:px-10 py-10 pb-20">
                  <div className="max-w-4xl space-y-8">
                    {activeTab === "overview" && (
                      <div className="space-y-8">

                        <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm relative overflow-hidden group">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-[#F1ECFB]/30 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-[#F1ECFB]/50 transition-colors"></div>

                          <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10">
                            <div>
                              <h4 className="text-lg font-bold text-[#402B69] mb-1">Performance Dashboard</h4>
                              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Metrics and Analytics</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Select
                                value={filterPeriod}
                                onValueChange={(value) => setFilterPeriod(value as FilterPeriod)}
                              >
                                <SelectTrigger className="w-[160px] h-11 text-sm font-bold border border-gray-200 rounded-xl bg-white text-[#402B69] focus:ring-4 focus:ring-[#885ABB]/10 transition-all shadow-sm">
                                  <SelectValue placeholder="Period" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border border-gray-100 shadow-xl rounded-xl z-[1100]">
                                  <SelectItem value="all">All time</SelectItem>
                                  <SelectItem value="today">Today</SelectItem>
                                  <SelectItem value="week">Week to date</SelectItem>
                                  <SelectItem value="month">This month</SelectItem>
                                  <SelectItem value="year">This year</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Metrics Cards */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                            {/* Sales Card */}
                            <div className="bg-[#F9F9F9] rounded-2xl p-6 border border-gray-100 hover:border-[#885ABB]/30 transition-all group/card">
                              <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-[#885ABB] shadow-sm">
                                    <h5 className="text-xs font-bold font-serif">£</h5>
                                  </div>
                                  <h5 className="text-sm font-bold text-[#402B69]">Total Sales</h5>
                                </div>
                                <Info size={16} className="text-gray-300 group-hover/card:text-[#885ABB] transition-colors" />
                              </div>
                              <div className="text-3xl font-bold text-[#402B69] tracking-tight">
                                £{totalSales.toFixed(2)}
                              </div>
                              <div className="text-[11px] text-[#885ABB] mt-4 font-bold uppercase tracking-wider bg-white rounded-lg py-1.5 px-3 inline-block shadow-sm">
                                {getFilterLabel(filterPeriod)}
                              </div>
                            </div>

                            {/* Appointments Card */}
                            <div className="bg-[#F9F9F9] rounded-2xl p-6 border border-gray-100 hover:border-[#885ABB]/30 transition-all group/card">
                              <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-[#885ABB] shadow-sm">
                                    <Info size={18} />
                                  </div>
                                  <h5 className="text-sm font-bold text-[#402B69]">Bookings</h5>
                                </div>
                                <Info size={16} className="text-gray-300 group-hover/card:text-[#885ABB] transition-colors" />
                              </div>
                              <div className="text-3xl font-bold text-[#402B69] tracking-tight">
                                {totalAppointments}
                              </div>
                              <div className="text-[11px] text-[#885ABB] mt-4 font-bold uppercase tracking-wider bg-white rounded-lg py-1.5 px-3 inline-block shadow-sm">
                                {getAppointmentsLabel(filterPeriod)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === "personal" && (
                      <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm space-y-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Full Name</label>
                            <p className="text-base font-bold text-[#402B69] bg-gray-50/50 p-4 rounded-xl border border-gray-50">{employee.name}</p>
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Email Address</label>
                            <p className="text-base font-bold text-[#402B69] bg-gray-50/50 p-4 rounded-xl border border-gray-50">{employee.email}</p>
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Mobile Number</label>
                            <p className="text-base font-bold text-[#402B69] bg-gray-50/50 p-4 rounded-xl border border-gray-50">
                              {formatMobile(employee.mobile, employee.calling_code)}
                            </p>
                          </div>
                          {employee.group && (
                            <div className="space-y-1.5">
                              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Group / Team</label>
                              <p className="text-base font-bold text-[#402B69] bg-gray-50/50 p-4 rounded-xl border border-gray-50">{employee.group}</p>
                            </div>
                          )}
                          {employee.address && (
                            <div className="sm:col-span-2 space-y-1.5">
                              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Physical Address</label>
                              <p className="text-base font-bold text-[#402B69] bg-gray-50/50 p-4 rounded-xl border border-gray-50">{employee.address}</p>
                            </div>
                          )}
                          {employee.phone && (
                            <div className="space-y-1.5">
                              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Phone Number</label>
                              <p className="text-base font-bold text-[#402B69] bg-gray-50/50 p-4 rounded-xl border border-gray-50">{employee.phone}</p>
                            </div>
                          )}
                          {employee.country && (
                            <div className="space-y-1.5">
                              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Country</label>
                              <p className="text-base font-bold text-[#402B69] bg-gray-50/50 p-4 rounded-xl border border-gray-50">{employee.country}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {activeTab === "workspace" && (
                      <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm space-y-10">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Business Role</label>
                            <p className="text-base font-bold text-[#402B69] bg-gray-50/50 p-4 rounded-xl border border-gray-50">{employee.role}</p>
                          </div>
                          {employee.status && (
                            <div className="space-y-1.5">
                              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Employment Status</label>
                              <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-50">
                                <span className={cn(
                                  "inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider",
                                  employee.status === "active"
                                    ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                                    : "bg-gray-100 text-gray-500 border border-gray-200"
                                )}>
                                  {employee.status}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="space-y-6">
                          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Assigned Services</label>
                          <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-50">
                            {employee.services && employee.services.length > 0 ? (
                              <div className="flex flex-wrap gap-3">
                                {employee.services.map((service, index) => (
                                  <span key={index} className="px-5 py-2.5 bg-white text-[#885ABB] rounded-xl text-sm font-bold shadow-sm border border-[#C4A6E1]/30">
                                    {service}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-400 text-sm font-medium italic">No services specifically assigned to this member.</p>
                            )}
                          </div>
                        </div>

                        {employee.locations && employee.locations.length > 0 && (
                          <div className="space-y-6">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Active Locations</label>
                            <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-50">
                              <div className="flex flex-wrap gap-3">
                                {employee.locations.map((location, index) => (
                                  <span key={index} className="px-5 py-2.5 bg-white text-[#402B69] rounded-xl text-sm font-bold shadow-sm border border-gray-200">
                                    {location.name || location}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === "pay" && (
                      <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Hourly Earnings</label>
                            <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-50">
                              <p className="text-2xl font-bold text-[#402B69]">
                                {(employee as any)?.employeeDetails?.rate && (employee as any)?.employeeDetails?.work_type === "hourly"
                                  ? `£${parseFloat((employee as any).employeeDetails.rate).toFixed(2)}`
                                  : "£0.00"}
                              </p>
                              <p className="text-[10px] text-gray-400 font-bold uppercase mt-1 tracking-wider">Per hour worked</p>
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">Commission Structure</label>
                            <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-50">
                              <p className="text-2xl font-bold text-[#885ABB]">
                                {(employee as any)?.employeeDetails?.commission
                                  ? `${(employee as any).employeeDetails.commission}%`
                                  : "None"}
                              </p>
                              <p className="text-[10px] text-gray-400 font-bold uppercase mt-1 tracking-wider">On booking revenue</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EmployeeDetailsSidebar;
